/**
 * A markdown body as plain words, and as the text a search reads.
 *
 * Both are read off the parsed tree, so what counts as a word here is what the
 * page draws as one. The search text is what an excerpt is cut from when the
 * phrase is among the words; a phrase held only by the source — a link's
 * address — is not here, and whoever cuts the excerpt shows the source for it.
 */

import {
  type MarkdownNode,
  nodesOf,
  parseMarkdown,
  wordsOf,
} from './markdown-tree'

/** The blocks that hold words themselves, and are read whole. */
const HOLDS_WORDS = new Set<MarkdownNode['type']>([
  'paragraph',
  'heading',
  'code',
  'html',
  'tableRow',
])

/** The blocks the page draws nothing for. */
const DRAWS_NOTHING = new Set<MarkdownNode['type']>([
  'definition',
  'thematicBreak',
])

/** A block's words: a row is its cells, a tab between them. */
function blockWords(node: MarkdownNode): string {
  if (node.type !== 'tableRow') return wordsOf(node)
  return node.children.map((cell) => wordsOf(cell).trim()).join('\t')
}

/**
 * The words of a body, block by block, with markdown's own syntax removed.
 *
 * What is kept is what a reader of the drawn body reads: a heading's words
 * without its hashes, a link's text without its address, a list item without
 * its marker, a table's cells without its bars. Code is kept exactly as
 * written. A blank line stands between blocks, and a single break between the
 * items of one list and the rows of one table.
 */
export function markdownPlainText(body: string): string {
  let out = ''
  let last: MarkdownNode | null | undefined

  const taken = (node: MarkdownNode) =>
    HOLDS_WORDS.has(node.type) || DRAWS_NOTHING.has(node.type)

  for (const { node, within } of nodesOf(parseMarkdown(body), taken)) {
    if (!HOLDS_WORDS.has(node.type)) continue
    const words = blockWords(node)
    if (last !== undefined) out += within && within === last ? '\n' : '\n\n'
    out += words
    last = within
  }
  return out.replace(/\r\n?/g, '\n').trim()
}

/**
 * What a search is matched against, and what its excerpt is cut from.
 *
 * The plain words on one line: a body is written hard-wrapped, so a phrase a
 * reader sees whole is split across two lines of the source wherever the
 * author's editor wrapped it. Case is left alone — folding it is the
 * matcher's, since an excerpt shows the words as they were written.
 */
export function markdownSearchText(body: string): string {
  return markdownPlainText(body).replace(/\s+/g, ' ').trim()
}
